import { Navigate, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useAuth } from '@/features/auth/stores/AuthContext'
import type { Permission, UserRole } from '@/features/auth/types/auth'

// Loading component
function AuthLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading...</p>
      </div>
    </div>
  )
}

// Protected Route component
interface ProtectedRouteProps {
  children: ReactNode
  requiredPermissions?: Permission[]
  requiredRoles?: UserRole[]
  fallback?: ReactNode
  redirectTo?: string
}

export function ProtectedRoute({ 
  children, 
  requiredPermissions = [], 
  requiredRoles = [],
  fallback,
  redirectTo = '/admin/login'
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasRole, hasAnyPermission } = useAuth()
  const location = useLocation()

  // Show loading while checking auth
  if (isLoading) {
    return <AuthLoading />
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />
  }

  // Check role requirements
  if (requiredRoles.length > 0 && !hasRole(requiredRoles)) {
    return fallback || (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    )
  }

  // Check permission requirements
  if (requiredPermissions.length > 0 && !hasAnyPermission(requiredPermissions)) {
    return fallback || (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Insufficient Permissions</h1>
          <p className="text-gray-600">You don't have the required permissions to access this page.</p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

// Admin Route wrapper
interface AdminRouteProps {
  children: ReactNode
  requiredPermissions?: Permission[]
}

export function AdminRoute({ children, requiredPermissions }: AdminRouteProps) {
  return (
    <ProtectedRoute 
      requiredRoles={['admin']} 
      requiredPermissions={requiredPermissions}
      redirectTo="/admin/login"
    >
      {children}
    </ProtectedRoute>
  )
}

// StaffRoute removed - only admin role exists now

// Public route that redirects authenticated users
interface PublicRouteProps {
  children: ReactNode
  redirectTo?: string
}

export function PublicRoute({ children, redirectTo = '/admin' }: PublicRouteProps) {
  const { isAuthenticated, isLoading, hasRole } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return <AuthLoading />
  }

  // Redirect authenticated admin users to admin dashboard
  // Use location.state to prevent redirect loops
  if (isAuthenticated && hasRole(['admin'])) {
    const from = (location.state as any)?.from?.pathname || redirectTo
    return <Navigate to={from} replace state={{ redirected: true }} />
  }

  return <>{children}</>
}

// Conditional rendering based on permissions
interface PermissionGateProps {
  children: ReactNode
  permissions?: Permission[]
  roles?: UserRole[]
  fallback?: ReactNode
  requireAll?: boolean // true = require ALL permissions/roles, false = require ANY
}

export function PermissionGate({ 
  children, 
  permissions = [], 
  roles = [],
  fallback = null,
  requireAll = false
}: PermissionGateProps) {
  const { hasPermission, hasRole, hasAnyPermission } = useAuth()

  // Check role requirements
  let hasRequiredRole = true
  if (roles.length > 0) {
    if (requireAll) {
      hasRequiredRole = roles.every(role => hasRole(role))
    } else {
      hasRequiredRole = hasRole(roles)
    }
  }

  // Check permission requirements
  let hasRequiredPermission = true
  if (permissions.length > 0) {
    if (requireAll) {
      hasRequiredPermission = permissions.every(permission => hasPermission(permission))
    } else {
      hasRequiredPermission = hasAnyPermission(permissions)
    }
  }

  // Show children if all requirements are met
  if (hasRequiredRole && hasRequiredPermission) {
    return <>{children}</>
  }

  return <>{fallback}</>
}

// Hook for route access checking
export function useRouteAccess() {
  const { canAccessRoute, hasPermission, hasRole } = useAuth()
  
  return {
    canAccessRoute,
    hasPermission,
    hasRole,
    checkAccess: (path: string, permissions?: Permission[], roles?: UserRole[]) => {
      if (!canAccessRoute(path)) return false
      
      if (permissions && permissions.length > 0) {
        if (!permissions.some(permission => hasPermission(permission))) {
          return false
        }
      }
      
      if (roles && roles.length > 0) {
        if (!hasRole(roles)) {
          return false
        }
      }
      
      return true
    }
  }
}